import { get, set } from 'lodash';
import { getRemoteFilters, mergeWithRemoteFilters, type Filter } from '@src/utils/filter';
import { ECO_WIDGET_TYPES } from '@src/components/widget-panel/config/eco';

type Response = Record<string, any>;

// update applied filters in trends persistency config
export const updateFiltersForTrends = async (response: Response = {}) => {
  const { configuration } = response;

  if (!configuration) return;

  // deprecate rca: https://conviva.atlassian.net/browse/HP-14322remove this after the feature is deprecated
  const widgetState = get(configuration, ['widgetState'], undefined);
  if (widgetState) {
    const { widgets, order } = widgetState;
    for (const [key, value] of Object.entries(widgets ?? {})) {
      if ((value as any)?.type === ECO_WIDGET_TYPES.autoRca) {
        delete widgetState.widgets[key];
        if (Array.isArray(order)) {
          widgetState.order = order.filter((k) => k !== key);
        }
      }
    }
  }

  const persistentFilter = configuration.filter;

  if (persistentFilter?.fid === undefined) return;

  const remoteFilters = await getRemoteFilters([persistentFilter.fid]);

  // Applied filter is already deleted
  if (!remoteFilters.length) {
    delete persistentFilter.fid;
    return;
  }

  const [latestFilter] = mergeWithRemoteFilters([persistentFilter], remoteFilters);
  configuration.filter = latestFilter;
};

// update applied filters in noc persistency config
export const updateFiltersForNOC = async (response: Response = {}) => {
  const persistentNoc = get(response, ['configuration', 'noc']);
  if (!persistentNoc) return;

  const trendsAppliedFilterPath = 'trends.appliedFilter';
  const filtersAppliedFilterPath = 'filters.appliedFilter';
  const trendsAppliedFilters = get(persistentNoc, trendsAppliedFilterPath, []);
  const filtersAppliedFilters = get(persistentNoc, filtersAppliedFilterPath, []);
  const fids = [
    ...(trendsAppliedFilters || []).map((it) => it.fid),
    ...(filtersAppliedFilters || []).map((it) => it.fid),
  ];
  const remoteFilters = await getRemoteFilters(fids);
  if (Array.isArray(trendsAppliedFilters) && trendsAppliedFilters.length) {
    const latestFilters = mergeWithRemoteFilters(trendsAppliedFilters, remoteFilters);
    set(persistentNoc, trendsAppliedFilterPath, latestFilters);
  }
  if (Array.isArray(filtersAppliedFilters) && filtersAppliedFilters.length) {
    const latestFilters = mergeWithRemoteFilters(filtersAppliedFilters, remoteFilters);
    set(persistentNoc, filtersAppliedFilterPath, latestFilters);
  }
};

// update applied filters in metric lens persistency config
export const updateFiltersForMetricLens = async (response: Response = {}) => {
  const appliedFiltersPath = 'configuration.ml.appliedFilters';
  const appliedFilters = get(response, appliedFiltersPath, []);
  if (Array.isArray(appliedFilters)) {
    const originalFilters = appliedFilters.map((it) => it.filter).filter(Boolean) as Filter[];
    const fids = originalFilters.map((it) => it.fid).filter(Boolean) as number[];

    const remoteFilters = await getRemoteFilters(fids);

    const nextAppliedFilters = appliedFilters.map((it) => {
      // only merge for saved filters
      if (it.filter?.fid) {
        const [filter] = mergeWithRemoteFilters([it.filter], remoteFilters);
        return {
          ...it,
          filter,
        };
      }
      return it;
    });
    set(response, appliedFiltersPath, nextAppliedFilters);
  }
};

// update applied filters overview persistency config
export const updateFiltersForOverview = async (response: Response = {}) => {
  const appliedFiltersPath = 'configuration.ovb.filter.appliedFilters';
  const breadcrumbFiltersPath = 'configuration.ovb.filter.breadcrumb';
  const appliedFilters = get(response, appliedFiltersPath);
  const breadcrumbFilters = get(response, breadcrumbFiltersPath);

  const fids = [
    ...(appliedFilters || []).map((it) => it.fid),
    ...(breadcrumbFilters || []).map((it) => it.fid),
  ];
  const remoteFilters = await getRemoteFilters(fids);
  if (Array.isArray(appliedFilters) && appliedFilters.length) {
    const latestFilters = mergeWithRemoteFilters(appliedFilters, remoteFilters);
    set(response, appliedFiltersPath, latestFilters);
  }
  if (Array.isArray(breadcrumbFilters) && breadcrumbFilters.length) {
    const latestFilters = mergeWithRemoteFilters(breadcrumbFilters, remoteFilters);
    set(response, breadcrumbFiltersPath, latestFilters);
  }
};
